import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Phone, MapPin, FileText, X, ShoppingBag, CreditCard, Banknote, Truck, Receipt, FileCheck, Search, Loader2, CheckCircle, Printer, Pencil, AlertTriangle, HandCoins } from 'lucide-react';
import { API_BASE_URL } from './config/api';
import type { InvoiceCustomer } from '../types/order';
import { useVatLookup } from '../hooks/useVatLookup';
import EditOrderItemsModal from './EditOrderItemsModal';
import OrderDriver from './delivery/OrderDriver';
import { useTrainingMode } from '../hooks/useTrainingMode';
import TrainingCompleteButton from './shared/TrainingCompleteButton';
import efoodLogo from '../assets/logos/logo_efood.png';
import boxLogo from '../assets/logos/logo_box.png';
import woltLogo from '../assets/logos/logo_wolt.png';
import storeLogo from '../assets/logos/logo.png';

interface OrderProduct {
  id: number;
  product_name: string;
  product_category: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  product_notes: string | null;
  integrator_id?: string;
  weight?: number;
  weight_unit?: string;
  // Αντιστοίχιση με τον κατάλογο του καταστήματος (docs/platform_name_mapping.md).
  // Τα πεδία έρχονται ήδη από το GET /orders/:storeId/orders/:orderId/products
  // (SELECT * μέσω του OrderProduct constructor) — εδώ απλώς τα δηλώνουμε ώστε
  // να φαίνεται στον ταμία ΓΙΑΤΙ μια γραμμή θα κοπεί με 13%.
  internal_product_id?: number | null;
  is_mapped?: boolean;
  vat?: number | string | null;
  mapping_reason?: string | null;
}

interface OrderPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: any;
  storeId: number;
  onComplete?: (orderId: number, fiscalData?: { documentType: 'receipt' | 'invoice'; invoiceCustomer?: InvoiceCustomer }) => void;
  showCompleteButton?: boolean;
  // Cancel is intentionally surfaced ONLY inside the preview modal — never on
  // the order list — so it can't be hit by accident. Parent decides eligibility
  // (permission, status) and provides the handler.
  showCancelButton?: boolean;
  onCancel?: (order: any) => void;
}

const OrderPreviewModal: React.FC<OrderPreviewModalProps> = ({ isOpen, onClose, order, storeId, onComplete, showCompleteButton = false, showCancelButton = false, onCancel }) => {
  const { t } = useTranslation('orders');
  const trainingModeEnabled = useTrainingMode(isOpen ? storeId : undefined);
  const canTrain = trainingModeEnabled && ['efood', 'wolt', 'box'].includes(order?.platform_name) && ['pending', 'accepted', 'ready'].includes(order?.status);
  const [products, setProducts] = useState<OrderProduct[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Fiscal document states
  const [documentType, setDocumentType] = useState<'receipt' | 'invoice'>('receipt');
  const { vatNumber: invoiceVatNumber, setVatNumber: setInvoiceVatNumber, customer: invoiceCustomer, setCustomer: setInvoiceCustomer, isLoading: lookingUpVat, error: vatLookupError, setError: setVatLookupError, lookup: vatLookup, reset: resetVatLookup } = useVatLookup();
  const [completing, setCompleting] = useState(false);
  const [reprintingTicket, setReprintingTicket] = useState(false);
  // Item editing (accepted platform orders, pre-receipt). editedTotal shadows
  // the stale order.total_amount prop after a successful edit.
  const [showEditItems, setShowEditItems] = useState(false);
  const [editedTotal, setEditedTotal] = useState<number | null>(null);

  const handleReprintTicket = async () => {
    if (!order?.id) return;
    setReprintingTicket(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders/${order.id}/reprint-ticket`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        alert(e.message || t('preview.reprintTicketFailed'));
      }
    } catch {
      alert(t('preview.reprintTicketFailed'));
    } finally {
      setReprintingTicket(false);
    }
  };

  useEffect(() => {
    if (isOpen && order) {
      fetchOrderProducts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, order]);

  const fetchOrderProducts = async () => {
    if (!order?.id) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('xentrix_token');
      
      const response = await fetch(`${API_BASE_URL}/api/orders/${storeId}/orders/${order.id}/products`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const responseText = await response.text();
      
      if (response.ok) {
        try {
          const data = JSON.parse(responseText);
          setProducts(data.products || []);
        } catch (parseError) {
          console.error('❌ JSON parse error:', parseError);
          console.error('❌ Response was:', responseText);
        }
      } else {
        console.error('❌ Response not OK:', response.status, responseText);
      }
    } catch (error) {
      console.error('❌ Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset states when modal opens
  useEffect(() => {
    if (isOpen) {
      setDocumentType('receipt');
      resetVatLookup();
      setCompleting(false);
      setShowEditItems(false);
      setEditedTotal(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Lookup VAT number using AADE API (via shared hook)
  const lookupVatNumber = async () => {
    if (!invoiceVatNumber || invoiceVatNumber.length < 9) {
      setVatLookupError(t('fiscal.vatValidation'));
      return;
    }
    await vatLookup(storeId);
  };

  // Generate receipt window (like TakeAway)
  const generateReceiptWindow = (fiscalData: any) => {
    const printLabel = t('common:print');
    const receiptWindow = window.open('', '_blank', 'width=400,height=600');
    if (!receiptWindow) {
      console.warn('⚠️ Popup blocked - cannot open receipt window');
      alert(t('popupBlocked'));
      return;
    }

    const total = parseFloat(order?.total_amount || '0');
    const vatAmount = total - (total / 1.24); // Approximate VAT

    receiptWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${documentType === 'invoice' ? 'Τιμολόγιο' : 'Απόδειξη'}</title>
        <style>
          body { font-family: 'Courier New', monospace; padding: 20px; max-width: 300px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
          .store-name { font-size: 18px; font-weight: bold; }
          .doc-type { font-size: 14px; font-weight: bold; margin: 10px 0; background: #f0f0f0; padding: 5px; }
          .item { display: flex; justify-content: space-between; margin: 5px 0; font-size: 12px; }
          .total { border-top: 1px dashed #000; margin-top: 10px; padding-top: 10px; font-weight: bold; }
          .footer { text-align: center; margin-top: 20px; font-size: 12px; border-top: 1px dashed #000; padding-top: 10px; }
          .mark { font-size: 10px; word-break: break-all; margin: 5px 0; }
          .success { color: green; font-weight: bold; }
          .customer-info { background: #f9f9f9; padding: 10px; margin: 10px 0; font-size: 11px; }
          @media print { button { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="store-name">XENTRIX FLOW</div>
          <div>${new Date().toLocaleString('el-GR')}</div>
        </div>
        
        <div class="doc-type">${documentType === 'invoice' ? 'ΤΙΜΟΛΟΓΙΟ' : 'ΑΠΟΔΕΙΞΗ ΛΙΑΝΙΚΗΣ'} #${fiscalData.series || '1'}-${fiscalData.documentNumber || ''}</div>
        
        ${documentType === 'invoice' && invoiceCustomer ? `
          <div class="customer-info">
            <strong>Στοιχεία Πελάτη:</strong><br>
            ${invoiceCustomer.name}<br>
            ΑΦΜ: ${invoiceCustomer.vat_number}<br>
            ${invoiceCustomer.address?.street || ''}
          </div>
        ` : ''}
        
        <div class="items">
          ${products.map(p => `
            <div class="item">
              <span>${p.weight ? `${p.weight.toFixed(3)} ${p.weight_unit === 'LT' ? 'lt' : 'kg'}` : `${p.quantity}x`} ${p.product_name}</span>
              <span>€${(parseFloat(String(p.total_price || 0))).toFixed(2)}</span>
            </div>
          `).join('')}
        </div>
        
        <div class="total">
          <div class="item">
            <span>ΣΥΝΟΛΟ</span>
            <span>€${total.toFixed(2)}</span>
          </div>
          <div class="item" style="font-size: 12px; font-weight: normal;">
            <span>ΦΠΑ</span>
            <span>€${vatAmount.toFixed(2)}</span>
          </div>
        </div>
        
        <div class="footer">
          <div>Πλατφόρμα: ${order?.platform_name?.toUpperCase() || 'N/A'}</div>
          <div>Πληρωμή: ${order?.payment_type === 'cash' ? 'ΜΕΤΡΗΤΑ' : order?.payment_type === 'credit' ? 'ΕΠΙ ΠΙΣΤΩΣΕΙ' : ['online','prepaid','card_online','credit_card'].includes(order?.payment_type) ? 'ONLINE' : 'ΚΑΡΤΑ'}</div>
          ${fiscalData.mark ? `<div class="mark">MARK: ${fiscalData.mark}</div>` : ''}
          ${fiscalData.uid ? `<div class="mark">UID: ${fiscalData.uid}</div>` : ''}
          ${fiscalData.success ? '<div class="success">✓ Καταχωρήθηκε στο myDATA</div>' : ''}
          <div style="margin-top: 10px;">Ευχαριστούμε!</div>
        </div>
        
        <button onclick="window.print()" style="width: 100%; margin-top: 20px; padding: 10px; cursor: pointer;">
          ${printLabel}
        </button>
      </body>
      </html>
    `);
    receiptWindow.document.close();
  };

  // Handle complete with fiscal data
  const handleComplete = async (isTraining = false) => {
    if (!order?.id) return;
    
    // For invoice, require customer data
    if (!isTraining && !order.is_training && documentType === 'invoice' && !invoiceCustomer) {
      setVatLookupError(t('fiscal.customerLookupRequired'));
      return;
    }

    setCompleting(true);
    try {
      // Call the complete endpoint directly to get the fiscal result
      const response = await fetch(`${API_BASE_URL}/api/orders/${order.id}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          fiscalDocumentType: documentType,
          isTraining: isTraining || (order.is_training === true && ['efood', 'wolt', 'box'].includes(order.platform_name)),
          invoiceCustomer: documentType === 'invoice' ? invoiceCustomer : undefined
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t('common:errors.completeOrder'));
      }

      // Show the browser receipt popup ONLY as a fallback: when the backend
      // could not queue a thermal print job (no active receipt printer). When a
      // printer handled it (printJobCreated), skip the popup.
      if (!result.order?.is_training && result.fiscalDocument?.success && !result.fiscalDocument.printJobCreated) {
        generateReceiptWindow(result.fiscalDocument);
      }

      // Close modal - parent component handles refresh via refreshOrders
      onClose();
    } catch (error: any) {
      console.error('Complete order error:', error);
      setVatLookupError(error.message || t('fiscal.completionError'));
    } finally {
      setCompleting(false);
    }
  };

  if (!isOpen) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('el-GR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Παραγγελία πλατφόρμας (efood/box/wolt): μόνο εκεί έχει νόημα η ένδειξη
  // «χωρίς αντιστοίχιση» — οι εσωτερικές γραμμές φτιάχνονται από τον κατάλογο.
  const isPlatformOrder = !!order?.platform_name && order.platform_name !== 'internal';

  // Ο ΦΠΑ έρχεται NUMERIC από τη βάση, δηλαδή string στο JSON ('13.00').
  const lineVatLabel = (p: OrderProduct) => {
    const n = Number(p.vat);
    return Number.isFinite(n) && n > 0 ? String(Math.round(n * 100) / 100) : '13';
  };

  // Parse customer info based on platform
  const orderJson = order?.order_json || {};
  let customerName = t('unknownCustomer');
  let customerSurname = '';
  let customerPhone = t('noPhone');
  let customerNotes = '';
  let customerAddress = t('noAddress');
  let floor = null;
  let doorbell = null;
  let paymentMethod = '';
  let deliveryMethod = '';
  
  if (order?.platform_name === 'internal') {
    // Internal order structure (delivery orders created in-app)
    // Customer data is in order_json.customer or directly on the order record
    const internalCustomer = orderJson.customer || {};
    customerName = internalCustomer.name || order?.customer_name || t('unknownCustomer');
    customerSurname = '';
    customerPhone = internalCustomer.phone || order?.customer_phone || t('noPhone');
    customerNotes = internalCustomer.notes || orderJson.notes || '';
    paymentMethod = orderJson.payment?.method || '';
    deliveryMethod = orderJson.deliveryMethod || order?.delivery_method || '';
    floor = internalCustomer.floor || null;
    doorbell = internalCustomer.doorbell || null;
    
    // Address from customer object or order record
    customerAddress = internalCustomer.address || order?.delivery_address || t('noAddress');
  } else if (order?.platform_name === 'wolt') {
    // Wolt order structure
    customerName = orderJson.consumer_name ? orderJson.consumer_name.split(' ')[0] : t('unknownCustomer');
    customerSurname = orderJson.consumer_name ? orderJson.consumer_name.split(' ').slice(1).join(' ') : '';
    customerPhone = orderJson.consumer_phone_number || t('noPhone');
    customerNotes = orderJson.consumer_comment || '';
    
    // Build Wolt address
    if (orderJson.delivery?.location) {
      const loc = orderJson.delivery.location;
      const parts = [];
      if (loc.street_address) parts.push(loc.street_address);
      if (loc.apartment) parts.push(`Apt: ${loc.apartment}`);
      if (loc.zip_code) parts.push(loc.zip_code);
      if (loc.city) parts.push(loc.city);
      customerAddress = parts.join(', ');
      
      // Extract floor/doorbell from formatted_address if available
      const formatted = loc.formatted_address || '';
      const floorMatch = formatted.match(/Floor:\s*([^\n]+)/);
      const doorbellMatch = formatted.match(/Name \/ Number on the door:\s*([^\n]+)/);
      floor = floorMatch ? floorMatch[1].trim() : null;
      doorbell = doorbellMatch ? doorbellMatch[1].trim() : null;
    }
  } else if (order?.platform_name === 'box') {
    // BOX order structure
    const user = orderJson.user || {};
    customerName = user.firstName || t('unknownCustomer');
    customerSurname = user.lastName || '';
    customerPhone = orderJson.orderContactPhone || t('noPhone');
    customerNotes = orderJson.comments || '';
    
    // Check if it's a takeaway order (BOX sends 'takeAway' with capital A)
    const isTakeaway = orderJson.orderShippingType === 'takeAway' || orderJson.orderShippingType === 'takeaway';
    
    // Build BOX address
    if (!isTakeaway && orderJson.address) {
      const addr = orderJson.address;
      const parts = [];
      if (addr.street) parts.push(addr.street);
      if (addr.streetNo) parts.push(addr.streetNo);
      if (addr.city) parts.push(addr.city);
      if (addr.postalCode) parts.push(addr.postalCode);
      customerAddress = parts.length > 0 ? parts.join(', ') : t('noAddress');
      
      floor = addr.floor || null;
      doorbell = addr.nameAtBell || null;
    } else if (isTakeaway) {
      customerAddress = `🍔 ${t('pickupAtStore')}`;
    }
  } else {
    // Efood order structure
    const efoodOrder = orderJson.orders?.[0] || {};
    const customer = efoodOrder.customer || {};
    
    customerName = customer.name || t('unknownCustomer');
    customerSurname = customer.surname || '';
    customerPhone = customer.telephone || customer.alternate_telephone || t('noPhone');
    customerNotes = customer.notes || '';
    floor = customer.floor || null;
    doorbell = customer.doorbell || null;
    
    // Build Efood address
    const parts = [];
    if (customer.street) parts.push(customer.street);
    if (customer.street_number) parts.push(customer.street_number);
    if (customer.area) parts.push(customer.area);
    if (customer.city) parts.push(customer.city);
    if (customer.postal_code) parts.push(customer.postal_code);
    customerAddress = parts.length > 0 ? parts.join(', ') : t('noAddress');
  }

  return (
    <div 
      className="cashier-overlay fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="cashier-dialog bg-surface-1 rounded-xl shadow-2xl border border-line max-w-3xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — flat, matches the app's slate look */}
        <div className="cashier-dialog-header px-5 py-4 border-b border-line flex items-center gap-3">
          <div className="w-11 h-11 bg-surface-2 rounded-xl flex items-center justify-center shrink-0 overflow-hidden">
            {order?.platform_name === 'efood' ? (
              <img src={efoodLogo} alt="Efood" className="w-8 h-8 object-contain" />
            ) : order?.platform_name === 'box' ? (
              <img src={boxLogo} alt="Box" className="w-8 h-8 object-contain" />
            ) : order?.platform_name === 'wolt' ? (
              <img src={woltLogo} alt="Wolt" className="w-8 h-8 object-contain" />
            ) : (
              <img src={storeLogo} alt="Store" className="w-8 h-8 object-contain" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-t1">#{order?.short_code || order?.platform_order_id?.slice(-6)}</h2>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase bg-surface-2 text-t2">
                {order?.platform_name === 'internal' ? 'IN-STORE' : order?.platform_name?.toUpperCase() || 'UNKNOWN'}
              </span>
              <span data-status={order?.status} className={`cashier-order-state cashier-order-status px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${
                order?.status === 'pending' ? 'bg-amber-500 text-white' :
                order?.status === 'accepted' ? 'bg-blue-500 text-white' :
                order?.status === 'ready' ? 'bg-teal-500 text-white' :
                order?.status === 'dispatched' ? 'bg-indigo-500 text-white' :
                order?.status === 'delivered' ? 'bg-green-600 text-white' :
                order?.status === 'completed' ? 'bg-surface-4 text-t1' :
                order?.status === 'rejected' ? 'bg-red-500 text-white' :
                order?.status === 'cancelled' ? 'bg-red-700 text-white' :
                'bg-surface-3 text-t2'
              }`}>
                {order?.status ? t(`status.${order.status}`, { defaultValue: order.status.toUpperCase() }) : 'UNKNOWN'}
              </span>
            </div>
            <p className="text-xs text-t3 mt-0.5">{formatDateTime(order?.order_timestamp)}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-t3 hover:text-t1 hover:bg-surface-2 rounded-lg transition-all shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* «Διανομέας: …» + «Αλλαγή διανομέα» (όπως στο Delivery): σε διανομή, με δικό μας διανομέα.
            Όχι έλεγχος order_type: στις πλατφόρμες ποικίλλει (π.χ. Wolt «preorder»). */}
        {order?.status === 'dispatched' && storeId > 0 && (
          <OrderDriver key={order.id} storeId={storeId} orderId={order.id} shortCode={order.short_code} />
        )}

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-surface-2 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-t4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <p className="text-t3 text-lg">{t('preview.noProducts')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {products.map((product, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-surface-2 dark:bg-surface-2/50 hover:bg-surface-2 transition-colors rounded-lg border border-line-strong"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="flex-shrink-0 w-auto min-w-8 h-8 px-2 bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-lg flex items-center justify-center text-sm font-medium">
                        {product.weight ? `${product.weight.toFixed(3)} ${product.weight_unit === 'LT' ? 'lt' : 'kg'}` : `${product.quantity}x`}
                      </span>
                      <div>
                        <p className="font-medium text-t1">
                          {product.product_name}
                        </p>
                        {/* Το προϊόν της πλατφόρμας δεν βρέθηκε στον κατάλογο:
                            η απόδειξη θα κοπεί με τον προεπιλεγμένο συντελεστή
                            (13%). Το δείχνουμε ΠΡΙΝ την ολοκλήρωση, γιατί μετά
                            το παραστατικό η διόρθωση θέλει ακυρωτικό.
                            ΕΞΑΙΡΕΣΗ 'ignored': η γραμμή έμεινε ασύνδετη επειδή
                            κάποιος το ΑΠΟΦΑΣΙΣΕ (φιλοδώρημα, σακούλα) — δεν
                            είναι εκκρεμότητα, οπότε δεν βγάζουμε προειδοποίηση. */}
                        {isPlatformOrder && product.is_mapped === false && product.mapping_reason !== 'ignored' && (
                          <span
                            title={t('mapping.unmappedHint', {
                              defaultValue: 'Το προϊόν δεν έχει συνδεθεί με τον κατάλογο του καταστήματος — η απόδειξη βγαίνει με ΦΠΑ 13%.'
                            })}
                            className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md border border-amber-500/30 bg-amber-500/15 text-amber-800 dark:text-amber-300 text-[11px] font-medium"
                          >
                            <AlertTriangle className="w-3 h-3 shrink-0" />
                            {t('mapping.unmappedChip', {
                              vat: lineVatLabel(product),
                              defaultValue: 'Χωρίς αντιστοίχιση · ΦΠΑ {{vat}}%'
                            })}
                          </span>
                        )}
                        {product.product_notes && (
                          <div className="text-sm text-t3 mt-1">
                            {(() => {
                              // Try to parse product notes if it's JSON (for BOX orders)
                              if (order?.platform_name === 'box') {
                                try {
                                  const selections = JSON.parse(product.product_notes);
                                  if (Array.isArray(selections) && selections.length > 0) {
                                    return selections.map((sel, idx) => (
                                      <div key={idx} className="py-0.5">
                                        {sel.title}: {sel.option}
                                        {sel.price_change > 0 && ` (+${(sel.price_change / 100).toFixed(2)}€)`}
                                      </div>
                                    ));
                                  }
                                } catch {
                                  // If not JSON, display as plain text
                                  if (product.product_notes && product.product_notes !== '[]') {
                                    return <p>{product.product_notes}</p>;
                                  }
                                }
                              } else if (order?.platform_name === 'wolt' && product.product_notes.includes('|')) {
                                // For Wolt orders, split options by | and display on separate lines
                                return product.product_notes.split('|').map((option, idx) => (
                                  <div key={idx} className="py-0.5">
                                    {option.trim()}
                                  </div>
                                ));
                              } else if (product.product_notes && product.product_notes !== '[]') {
                                // For other platforms, display as is (but hide empty arrays)
                                return <p>{product.product_notes}</p>;
                              }
                              return null;
                            })()}
                          </div>
                        )}
                        
                        {/* Display materials/ingredients from order_json */}
                        {(() => {
                          const efoodOrder = orderJson.orders?.[0] || {};
                          const efoodProduct = efoodOrder.products?.find((p: any) => 
                            p.name === product.product_name || p.integrator_id === product.integrator_id
                          );
                          const materials = efoodProduct?.materials || [];
                          
                          if (materials.length > 0) {
                            return (
                              <div className="mt-2">
                                <p className="text-xs text-t4 mb-1">Υλικά:</p>
                                <div className="flex flex-wrap gap-1">
                                  {materials.map((material: any, idx: number) => {
                                    const isXoris = material.name.includes('ΧΩΡΙΣ');
                                    return (
                                      <span
                                        key={idx}
                                        className={`inline-block px-2 py-0.5 text-xs rounded-md border ${
                                          isXoris 
                                            ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30' 
                                            : 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30'
                                        }`}
                                      >
                                        {material.name}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        {product.product_category && product.product_category !== 'Other' && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-surface-3 text-t2 text-xs rounded-md">
                            {product.product_category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="font-semibold text-t1">
                      {formatCurrency(product.total_price)}
                    </p>
                    {product.quantity > 1 && (
                      <p className="text-sm text-t3">
                        {formatCurrency(product.unit_price)} /τεμ
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Order Summary */}
          {products.length > 0 && (
            <div className="mt-6 space-y-4">
              {/* Customer Info - Compact */}
              <div className="bg-surface-2 dark:bg-surface-2/50 rounded-lg p-3 border border-line-strong">
                <div className="flex items-center justify-between flex-wrap gap-x-6 gap-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-t3" />
                    <span className="font-medium text-t1">{customerName} {customerSurname}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-t3" />
                    <span className="text-t2">{customerPhone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-t3" />
                    <span className="text-t2">{customerAddress}</span>
                    {floor && <span className="text-xs text-t3">(Όροφος: {floor})</span>}
                    {doorbell && <span className="text-xs text-t3">(Κουδ.: {doorbell})</span>}
                  </div>
                  {order?.platform_name === 'internal' && paymentMethod && (
                    <div className="flex items-center gap-2">
                      {paymentMethod === 'cash' ? (
                        <Banknote className="w-4 h-4 text-t3" />
                      ) : paymentMethod === 'credit' ? (
                        <HandCoins className="w-4 h-4 text-t3" />
                      ) : (
                        <CreditCard className="w-4 h-4 text-t3" />
                      )}
                      {/* «credit» = «Επί πιστώσει» (κωδ. 5), όχι πιστωτική κάρτα */}
                      {paymentMethod === 'credit'
                        ? <span className="text-t2">{t('common:payment.credit')}</span>
                        : <span className="text-t2 capitalize">{paymentMethod}</span>}
                    </div>
                  )}
                  {order?.platform_name === 'internal' && deliveryMethod && (
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-t3" />
                      <span className="text-t2 capitalize">{deliveryMethod === 'self' ? 'Δική μας διανομή' : deliveryMethod}</span>
                    </div>
                  )}
                </div>
                {customerNotes && (
                  <div className="mt-2 pt-2 border-t border-line-strong flex items-start gap-2 text-sm">
                    <FileText className="w-4 h-4 text-yellow-700 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <span className="text-yellow-800 dark:text-yellow-300">{customerNotes}</span>
                  </div>
                )}
              </div>
              
              {/* Total */}
              <div className="rounded-xl p-4 bg-surface-2 dark:bg-surface-2/60 border border-line-strong">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 bg-surface-3 rounded-xl flex items-center justify-center">
                      <ShoppingBag className="w-5 h-5 text-t2" />
                    </div>
                    <div>
                      <p className="text-t2 text-sm font-medium">{t('preview.orderTotal')}</p>
                      <p className="text-t4 text-xs">{products.length} {t('preview.items')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Edit items: platform orders between acceptance and completion —
                        the receipt is generated at completion, so this is the window
                        where a phone-agreed product swap can still be reflected. */}
                    {showCompleteButton && order?.platform_name !== 'internal' &&
                      ['accepted', 'ready', 'dispatched'].includes(order?.status) && (
                      <button
                        type="button"
                        onClick={() => setShowEditItems(true)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-500/40 text-amber-800 dark:text-amber-300 text-sm font-medium hover:bg-amber-500/10 active:bg-amber-500/20"
                      >
                        <Pencil className="w-4 h-4" />
                        {t('edit.button')}
                      </button>
                    )}
                    <span className="text-t1 font-bold text-2xl">
                      {formatCurrency(
                        editedTotal ??
                        (order?.total_amount ||
                        products.reduce((sum, p) => sum + p.total_price, 0))
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Fiscal Document Section - Only show for platform orders with complete button */}
              {showCompleteButton && order?.platform_name !== 'internal' && !order?.is_training && (
                <div className="bg-surface-2 dark:bg-surface-2/50 rounded-xl p-4 border border-line-strong space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-t1 flex items-center gap-2">
                      <FileCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      {t('fiscal.document')}
                    </h4>
                    
                    {/* Toggle Receipt/Invoice */}
                    <div className="flex bg-surface-3 rounded-lg p-1">
                      <button
                        onClick={() => {
                          setDocumentType('receipt');
                          setInvoiceCustomer(null);
                          setVatLookupError(null);
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                          documentType === 'receipt'
                            ? 'bg-surface-1 text-blue-600 dark:text-blue-400 shadow-sm'
                            : 'text-t3 hover:text-t2'
                        }`}
                      >
                        <Receipt className="w-4 h-4" />
                        {t('fiscal.receipt')}
                      </button>
                      <button
                        onClick={() => setDocumentType('invoice')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                          documentType === 'invoice'
                            ? 'bg-surface-1 text-blue-600 dark:text-blue-400 shadow-sm'
                            : 'text-t3 hover:text-t2'
                        }`}
                      >
                        <FileText className="w-4 h-4" />
                        {t('fiscal.invoice')}
                      </button>
                    </div>
                  </div>

                  {/* Invoice VAT Lookup */}
                  {documentType === 'invoice' && (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder={t('fiscal.vatPlaceholder')}
                            value={invoiceVatNumber}
                            onChange={(e) => setInvoiceVatNumber(e.target.value.replace(/\D/g, ''))}
                            onKeyDown={(e) => e.key === 'Enter' && lookupVatNumber()}
                            className="w-full px-3 py-2 bg-surface-2 border border-line-strong rounded-lg text-sm text-t1 placeholder-t3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            maxLength={9}
                          />
                        </div>
                        <button
                          onClick={lookupVatNumber}
                          disabled={lookingUpVat || invoiceVatNumber.length < 9}
                          className="cashier-primary px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-surface-3 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                        >
                          {lookingUpVat ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Search className="w-4 h-4" />
                          )}
                          {t('fiscal.vatSearch')}
                        </button>
                      </div>

                      {vatLookupError && (
                        <div className="text-sm text-red-700 dark:text-red-300 bg-red-500/15 border border-red-500/30 px-3 py-2 rounded-lg">
                          {vatLookupError}
                        </div>
                      )}

                      {invoiceCustomer && (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 space-y-2">
                          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">{t('fiscal.customerDetails')}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-t3">{t('fiscal.vatNumber')}</span>
                              <span className="ml-2 font-medium text-t1">{invoiceCustomer.vat_number}</span>
                            </div>
                            <div>
                              <span className="text-t3">{t('fiscal.businessName')}</span>
                              <span className="ml-2 font-medium text-t1">{invoiceCustomer.name}</span>
                            </div>
                            {invoiceCustomer.address && (
                              <div className="col-span-2">
                                <span className="text-t3">{t('fiscal.addressLabel')}</span>
                                <span className="ml-2 text-t2">
                                  {[
                                    invoiceCustomer.address.street,
                                    invoiceCustomer.address.number,
                                    invoiceCustomer.address.postal_code,
                                    invoiceCustomer.address.city
                                  ].filter(Boolean).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Receipt info */}
                  {documentType === 'receipt' && (
                    <div className="text-sm text-blue-700 dark:text-blue-300 bg-blue-500/10 border border-blue-500/30 px-3 py-2 rounded-lg flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      {t('fiscal.receiptCreditInfo')}
                    </div>
                  )}
                </div>
              )}

              {/* Complete Button */}
              {showCompleteButton && ['pending', 'accepted', 'ready', 'dispatched'].includes(order?.status) && (
                <TrainingCompleteButton
                  key={`${order?.id}-${documentType}`}
                  trainingEnabled={canTrain}
                  isTraining={order?.is_training === true && ['efood', 'wolt', 'box'].includes(order?.platform_name)}
                  onComplete={handleComplete}
                  disabled={completing || (!canTrain && !order?.is_training && documentType === 'invoice' && !invoiceCustomer)}
                  className="w-full py-3 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  {completing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('fiscal.completing')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      {t('fiscal.completeOrder')}
                    </>
                  )}
                </TrainingCompleteButton>
              )}

              {/* Cancel Order — placed inside the modal (not in the list) so it
                  can't be misclicked. Shown for any non-cancelled/non-rejected
                  order (incl. completed/delivered — cancelling those is gated to
                  admins via showCancelButton). Cancelling auto-cancels the
                  receipt if one was issued. */}
              {showCancelButton && order && !['cancelled', 'rejected'].includes(order?.status) && (
                <button
                  onClick={() => { onCancel?.(order); onClose(); }}
                  className="w-full mt-2 py-2.5 rounded-xl text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700/60 bg-red-50 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 font-medium text-sm transition-all"
                >
                  {t('cancelOrder.button', { ns: 'orders' })}
                </button>
              )}

              {/* Reprint the kitchen/delivery ticket on demand */}
              <button
                onClick={handleReprintTicket}
                disabled={reprintingTicket}
                className="w-full mt-2 py-2.5 rounded-xl text-t2 border border-line-strong bg-surface-2 dark:bg-surface-2/60 hover:bg-surface-2 font-medium text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {reprintingTicket ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                {t('preview.reprintTicket')}
              </button>
            </div>
          )}
        </div>
      </div>

      <EditOrderItemsModal
        isOpen={showEditItems}
        onClose={() => setShowEditItems(false)}
        order={order}
        storeId={storeId}
        orderItems={products}
        onSaved={(newTotal) => {
          setEditedTotal(newTotal);
          fetchOrderProducts();
        }}
      />
    </div>
  );
};

export default OrderPreviewModal;
